import { hoursToMinutes, minutesToHours } from '@acme/common'
import { Row } from '@tanstack/react-table'
import { explodirLinhas, LinhaExplodida } from './explodir-linhas'
import {
    LABEL_NIVEL,
    LinhaGranularPontoDiario,
    NivelPontoDiario,
    ORDEM_NIVEIS,
} from './niveis-agrupamento'

/**
 * Campo oculto que carrega a chave de cada nível — é o que entra no
 * `groupingModel` do TanStack (agrupa por `field` de coluna, não pelo nível).
 */
export const CAMPO_POR_NIVEL: Record<NivelPontoDiario, string> = {
    centroCusto: '_centroCustoNome',
    local: '_localNome',
    funcionario: '_funcionarioNome',
    dia: '_diaLabel',
}

/**
 * Menor contrato reutilizável para adaptadores tipados de agrupamento multinível.
 * Define níveis válidos, ordem fixa, mapeamento de chave e rótulo, agregação e expansão.
 */
export interface AdaptadorAgrupamentoRelatorio<
    TNivel extends string,
    TLinha = any,
    TLinhaExplodida = TLinha,
> {
    /** Níveis válidos para agrupamento neste relatório. */
    readonly niveisValidos: readonly TNivel[]
    /** Ordem fixa de precedência de agrupamento hierárquico. */
    readonly ordemNiveis: readonly TNivel[]
    /** Rótulo legível para cada nível (UI/chips/menu). */
    readonly labelNivel: Record<TNivel, string>
    /** Campo físico (coluna oculta) na linha explodida usado pelo groupingModel do TanStack. */
    readonly campoPorNivel: Record<TNivel, string>
    /** Deriva a lista de campos para o groupingModel respeitando ordemNiveis fixa. */
    obterGroupingModel: (niveisAtivos: Set<TNivel>) => string[]
    /** Regra de expansão: grupo terminal é agregado puro (sem drill-down). */
    podeExpandirLinha: (
        row: { depth: number },
        totalNiveisAtivos: number,
    ) => boolean
    /** Fan-out opcional para materializar produto cartesiano de vínculos múltiplos. */
    explodirLinhas?: (
        linhas: TLinha[],
        niveisAtivos: Set<TNivel>,
    ) => TLinhaExplodida[]
}

/**
 * Deriva a lista de campos de agrupamento a partir dos níveis marcados e da ordem fixa.
 */
export function derivarGroupingModel<TNivel extends string>(
    niveisAtivos: Set<TNivel>,
    ordemNiveis: readonly TNivel[],
    campoPorNivel: Record<TNivel, string>,
): string[] {
    return ordemNiveis
        .filter(n => niveisAtivos.has(n))
        .map(n => campoPorNivel[n])
}

/**
 * Guard para getRowCanExpand: grupo terminal é agregado puro, sem drill-down.
 */
export function podeExpandirGrupoTerminal(
    row: { depth: number },
    totalNiveisAtivos: number,
): boolean {
    return row.depth + 1 < totalNiveisAtivos
}

/**
 * Filtra getLeafRows() para reter apenas as linhas-folha reais (descarta subgrupos intermediários).
 */
export function linhasFolhaDoGrupo<T = any>(tableRow: Row<T>): Row<T>[] {
    return tableRow.getLeafRows().filter(r => !r.getIsGrouped())
}

/**
 * Agregação tipada de horas (HH:mm) para linhas de grupo.
 */
export function somarCampoDoGrupo(tableRow: Row<any>, campo: string): string {
    return minutesToHours(
        linhasFolhaDoGrupo(tableRow).reduce(
            (acc: number, r: Row<any>) =>
                acc + hoursToMinutes(r.original[campo]),
            0,
        ),
    )
}

/**
 * Adaptador de referência do Ponto Diário em conformidade com o contrato reutilizável.
 */
export const adaptadorPontoDiario: AdaptadorAgrupamentoRelatorio<
    NivelPontoDiario,
    LinhaGranularPontoDiario,
    LinhaExplodida
> = {
    niveisValidos: ORDEM_NIVEIS,
    ordemNiveis: ORDEM_NIVEIS,
    labelNivel: LABEL_NIVEL,
    campoPorNivel: CAMPO_POR_NIVEL,
    obterGroupingModel: (niveisAtivos: Set<NivelPontoDiario>) =>
        derivarGroupingModel(niveisAtivos, ORDEM_NIVEIS, CAMPO_POR_NIVEL),
    podeExpandirLinha: podeExpandirGrupoTerminal,
    explodirLinhas,
}
